import { useAiAccess } from '../ai/useAiAccess';
import { useEffect, useId, useRef, useState } from 'react';
import MenuPanel from './MenuPanel';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { api } from '../api/client';
import { useT } from '../i18n/i18n';
import AiCreditsBadge from './AiCreditsBadge';

type Props = {
  onDeleteAccount: () => void;
};

export default function UserMenu({ onDeleteAccount }: Props) {
  const { user, logout } = useAuth();
  const t = useT();
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (wrapRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const [admin, setAdmin] = useState(false);
  const { access: aiAccess, refresh: refreshAi } = useAiAccess();
  const developer = !!aiAccess?.developer;
  useEffect(() => { if (open) refreshAi(); }, [open, refreshAi]);
  useEffect(() => {
    if (!user) return;
    api<{ admin: boolean }>('/concepts/approvals/me', { token: true }).then((m) => setAdmin(m.admin)).catch(() => setAdmin(false));
  }, [user]);

  const label = user?.displayName || user?.email || t('Account');

  return (
    <div className="usermenu" ref={wrapRef}>
      <button
        ref={triggerRef}
        type="button"
        className="usermenu-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((v) => !v)}
      >
        {label}
        <span className="usermenu-caret" aria-hidden="true">▾</span>
      </button>

      {open && (
        <MenuPanel anchor={triggerRef} panelRef={panelRef} id={panelId} className="usermenu-panel">
          <div className="usermenu-who">
            <div className="usermenu-who-text">
              <span className="name">{user?.displayName ?? t('Signed in')}</span>
              {user?.email && <span className="email">{user.email}</span>}
            </div>
            <AiCreditsBadge access={aiAccess} />
          </div>

          <Link
            className="usermenu-item"
            role="menuitem"
            to="/account/subscription"
            onClick={() => setOpen(false)}
          >
            {t('Subscription')}
          </Link>
          {developer && (
            <Link
              className="usermenu-item"
              role="menuitem"
              to="/admin/testers"
              onClick={() => setOpen(false)}
            >
              {t('Testers and AI credits')}
            </Link>
          )}
          {admin && (
            <Link
              className="usermenu-item"
              role="menuitem"
              to="/admin/promo"
              onClick={() => setOpen(false)}
            >
              {t('Promotion codes')}
            </Link>
          )}
          {admin && (
            <Link
              className="usermenu-item"
              role="menuitem"
              to="/admin/game-types"
              onClick={() => setOpen(false)}
            >
              {t('Game types')}
            </Link>
          )}
          {admin && (
            <Link
              className="usermenu-item"
              role="menuitem"
              to="/admin/dpa"
              onClick={() => setOpen(false)}
            >
              {t('Data processing agreement (draft)')}
            </Link>
          )}
          <Link
            className="usermenu-item"
            role="menuitem"
            to="/settings"
            onClick={() => setOpen(false)}
          >
            {t('Settings')}
          </Link>
          <button
            type="button"
            className="usermenu-item"
            role="menuitem"
            onClick={() => { setOpen(false); logout(); }}
          >
            {t('Sign out')}
          </button>
          <button
            type="button"
            className="usermenu-item danger"
            role="menuitem"
            onClick={() => { setOpen(false); onDeleteAccount(); }}
          >
            {t('Delete account')}
          </button>
        </MenuPanel>
      )}
    </div>
  );
}
